// YakuToggle — lists the master YAKU_CATALOG. Each row: an enable checkbox, a han stepper (disabled
// + a "条件: エンジン定義" badge for structural yaku whose han is intrinsic and NOT overridable), the
// yaku's example hand rendered via TileView, and a Verify button that runs the example through the
// engine (detectYaku/score) to prove it fires. Edits patch the store (save→reflect).
import { useMemo, useState } from 'react';
import type { YakuDefMeta } from '@mj/protocol';
import { useStudioStore, useStudioApi } from '../useStore';
import { TileRow } from '../render/TileView';
import { verifyYaku, type VerifyResult } from '../verify';
import { makeNameOf, makeYakumanName } from '../yakuName';

function StructuralBadge() {
  return (
    <span
      title="成立条件（牌姿の判定）はエンジン定義。翻は自由に編集できます。"
      style={{ fontSize: 10, background: '#eee', color: '#555', borderRadius: 4, padding: '1px 5px', marginLeft: 6 }}
    >
      条件: エンジン定義
    </span>
  );
}

// 13翻=役満, 26翻=ダブル役満, 39翻=トリプル役満, それ以上は n倍役満.
function yakumanTierLabel(mult: number): string {
  if (mult <= 1) return '役満';
  if (mult === 2) return 'ダブル役満';
  if (mult === 3) return 'トリプル役満';
  return `${mult}倍役満`;
}

function YakuRow({ def }: { def: YakuDefMeta }) {
  const api = useStudioApi();
  const catalog = useStudioStore((s) => s.catalog);
  const nameOf = makeNameOf(catalog);
  const ymName = makeYakumanName(catalog);
  const entry = useStudioStore((s) => s.ruleSet.enabledYaku[def.id]);
  const enabled = entry?.enabled ?? false;
  const structural = def.tunable === 'structural';
  const effectiveHan = entry?.hanOverride ?? def.defaultHan;
  const [verify, setVerify] = useState<VerifyResult | null>(null);
  const [busy, setBusy] = useState(false);

  const runVerify = async () => {
    setBusy(true);
    const st = api.getState();
    try {
      const r = await verifyYaku(st.engine, def, st.ruleSet, st.catalog);
      setVerify(r);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        border: '1px solid #e2e2e2',
        borderRadius: 6,
        padding: 8,
        marginBottom: 6,
        background: enabled ? '#fff' : '#fafafa',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <input type="checkbox" checked={enabled} onChange={(e) => api.getState().toggleYaku(def.id, e.target.checked)} />
        <strong style={{ minWidth: 130 }}>{def.name.ja}</strong>
        <span style={{ fontSize: 12, color: '#888', minWidth: 120 }}>{def.name.en}</span>
        {def.kind === 'yakuman' ? (
          // Yakuman value is editable in 13-han steps: 13翻=役満, 26翻=ダブル, 39翻=トリプル.
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <button
              disabled={!enabled}
              onClick={() => api.getState().setHanOverride(def.id, Math.max(13, effectiveHan - 13))}
              style={{ width: 22 }}
            >
              −
            </button>
            <span style={{ minWidth: 64, textAlign: 'center', fontSize: 11, color: '#a33' }}>
              {effectiveHan}翻 {yakumanTierLabel(Math.floor(effectiveHan / 13))}
            </span>
            <button
              disabled={!enabled}
              onClick={() => api.getState().setHanOverride(def.id, effectiveHan + 13)}
              style={{ width: 22 }}
            >
              +
            </button>
          </span>
        ) : (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 12 }}>han</span>
            <button
              disabled={!enabled}
              onClick={() => api.getState().setHanOverride(def.id, Math.max(1, effectiveHan - 1))}
              style={{ width: 22 }}
            >
              −
            </button>
            <span style={{ minWidth: 18, textAlign: 'center' }}>{effectiveHan}</span>
            <button
              disabled={!enabled}
              onClick={() => api.getState().setHanOverride(def.id, effectiveHan + 1)}
              style={{ width: 22 }}
            >
              +
            </button>
            {structural && <StructuralBadge />}
          </span>
        )}
        {def.category === 'localFamous' && (
          <span style={{ fontSize: 10, background: '#eef6ee', color: '#2b6e2b', borderRadius: 4, padding: '1px 5px' }}>local</span>
        )}
        {def.category === 'dora' && (
          <span title="ドラ：役ではなく翻のみ加算。赤五1枚あたりの翻数を編集できます。" style={{ fontSize: 10, background: '#fdecec', color: '#c0202a', borderRadius: 4, padding: '1px 5px' }}>ドラ</span>
        )}
        <button onClick={runVerify} disabled={busy} style={{ marginLeft: 'auto' }}>
          {busy ? '…' : 'Verify'}
        </button>
      </div>

      <div style={{ marginTop: 6 }}>
        <TileRow tiles={def.exampleHand} size={26} />
      </div>

      {verify && (
        <div style={{ marginTop: 6, fontSize: 12 }}>
          {verify.error ? (
            <span style={{ color: '#c0202a' }}>error: {verify.error}</span>
          ) : (
            <span>
              <span style={{ color: verify.targetFired ? '#2b6e2b' : '#c0202a', fontWeight: 700 }}>
                {verify.targetFired ? '✓ fires' : '✗ did not fire'}
              </span>
              {'  '}
              fired: {[...verify.yakuman.map((y) => `${ymName(y.id, y.mult)}(役満×${y.mult})`), ...verify.fired.map((y) => `${nameOf(y.id)}(${y.han})`)].join(', ') || '—'}
              {'  '}| {verify.yakuman.length ? 'yakuman' : `${verify.han}han ${verify.fu}fu`} → {verify.points}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export function YakuToggle() {
  const catalog = useStudioStore((s) => s.catalog);
  const [filter, setFilter] = useState<'all' | 'standard' | 'localFamous' | 'dora' | 'yakuman'>('all');

  const shown = useMemo(() => {
    return catalog.filter((d) => {
      if (filter === 'all') return true;
      if (filter === 'yakuman') return d.kind === 'yakuman';
      return d.category === filter;
    });
  }, [catalog, filter]);

  return (
    <div style={{ maxWidth: 900 }}>
      <div style={{ marginBottom: 8, display: 'flex', gap: 6, alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: '#666' }}>filter:</span>
        {(['all', 'standard', 'localFamous', 'dora', 'yakuman'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={{ fontWeight: filter === f ? 700 : 400, padding: '2px 8px' }}
          >
            {f}
          </button>
        ))}
        <span style={{ marginLeft: 'auto', fontSize: 12, color: '#888' }}>{shown.length} yaku</span>
      </div>
      {shown.map((def) => (
        <YakuRow key={def.id} def={def} />
      ))}
    </div>
  );
}
